/**
 * Battle API Client
 *
 * HTTP client for battle-related API endpoints. Handles battle creation,
 * joining, quick play, and browsing open battles.
 */

import { parseBattleApiError } from './battle-api-errors';
import { getApiUrl, getAuthToken } from './api-client';

/**
 * Make authenticated API request (with battle-specific error parsing)
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();
  const url = `${getApiUrl()}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw parseBattleApiError(data);
  }

  return data;
}

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Battle player information
 */
export interface BattlePlayer {
  userId: string;
  displayName: string;
  joinedAt: string;
}

/**
 * Battle entity returned from API
 */
export interface Battle {
  battleId: string;
  battleKey: string;
  battleName: string;
  matchId: string;
  status: 'waiting' | 'starting' | 'active' | 'completed';
  isPublic: boolean;
  players: {
    host: BattlePlayer;
    opponent?: BattlePlayer;
  };
  createdAt: string;
  settings?: {
    maxPlayers: number;
    minCreatures: number;
    maxCreatures: number;
  };
}

/**
 * Response from creating a battle
 */
export interface CreateBattleResponse {
  success: true;
  battleId: string;
  battleKey: string;
  matchId: string;
  playerId: 'player1' | 'player2';
}

/**
 * Response from joining a battle
 */
export interface JoinBattleResponse {
  success: true;
  battleId: string;
  matchId: string;
  playerId: 'player1' | 'player2';
}

/**
 * Response from quick play
 */
export interface QuickPlayResponse {
  success: true;
  action: 'joined' | 'created';
  battleId: string;
  battleKey: string;
  matchId: string;
  playerId: 'player1' | 'player2';
}

/**
 * Response from getting open battles
 */
export interface BattleListResponse {
  battles: Battle[];
  page: number;
  limit: number;
  total: number;
}

/**
 * Single battle entry from history
 */
export interface BattleHistoryEntry {
  battleId: string;
  battleName: string;
  completedAt: string | null;
  opponentDisplayName: string;
  userResult: 'win' | 'loss' | 'draw';
  resultReason?: string;
}

/**
 * Battle stats summary
 */
export interface BattleStats {
  wins: number;
  losses: number;
  draws: number;
}

/**
 * Response from getting battle history
 */
export interface BattleHistoryResponse {
  battles: BattleHistoryEntry[];
  stats: BattleStats;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Create a new battle
 *
 * @param battleName - Name for the battle
 * @param isPublic - Whether battle is public (default: true)
 * @param password - Password for closed battles (required if isPublic is false)
 * @param creatureLimit - Number of creatures per side (3-6, default: 3)
 * @returns Battle creation response with battleKey and matchId
 * @throws InsufficientCreaturesError - User doesn't have enough creatures
 * @throws ActiveBattleExistsError - User already in an active battle
 *
 * OCBV Update: Now supports password-protected closed battles
 * Creature Limit Update: Now supports variable battle sizes (3v3 to 6v6)
 *
 * @example
 * ```typescript
 * // Open battle with default 3v3
 * const result = await createBattle('Epic Showdown', true);
 *
 * // Closed battle with password and 5v5
 * const result = await createBattle('Private Match', false, 'secret123', 5);
 *
 * navigate(`/deployment?matchId=${result.matchId}&playerId=${result.playerId}`);
 * ```
 */
export async function createBattle(
  battleName: string,
  isPublic: boolean = true,
  password?: string,
  creatureLimit: number = 3
): Promise<CreateBattleResponse> {
  return apiRequest<CreateBattleResponse>('/api/battles', {
    method: 'POST',
    body: JSON.stringify({
      battleName,
      isPublic,
      creatureLimit,
      ...(password && { password })
    })
  });
}

/**
 * Join a battle by its 6-character key
 *
 * @param battleKey - 6-character battle key (uppercase alphanumeric)
 * @returns Join response with matchId and playerId
 * @throws Error - Invalid battle key format
 * @throws BattleNotFoundError - Battle with key doesn't exist
 * @throws BattleAlreadyFilledError - Battle is full or already started
 * @throws CannotJoinOwnBattleError - Trying to join own battle
 * @throws InsufficientCreaturesError - User doesn't have enough creatures
 *
 * @deprecated Use joinBattle(battleId, password?) instead
 *
 * @example
 * ```typescript
 * const result = await joinBattleByKey('ABC123');
 * navigate(`/deployment?matchId=${result.matchId}&playerId=${result.playerId}`);
 * ```
 */
export async function joinBattleByKey(
  battleKey: string
): Promise<JoinBattleResponse> {
  // Validate battle key format: 6 uppercase alphanumeric characters
  if (!/^[A-Z0-9]{6}$/.test(battleKey)) {
    throw new Error('Invalid battle key format. Must be 6 uppercase letters/numbers.');
  }

  return apiRequest<JoinBattleResponse>('/api/battles/join', {
    method: 'POST',
    body: JSON.stringify({ battleKey })
  });
}

/**
 * Join a battle by its ID with optional password
 *
 * OCBV-011: New endpoint for password-protected battle joining
 *
 * @param battleId - UUID of the battle to join
 * @param password - Password for closed battles (optional for open battles)
 * @returns Join response with matchId and playerId
 * @throws BattleNotFoundError - Battle doesn't exist
 * @throws BattleAlreadyFilledError - Battle is full or already started
 * @throws CannotJoinOwnBattleError - Trying to join own battle
 * @throws IncorrectPasswordError - Wrong password for closed battle (401)
 * @throws InsufficientCreaturesError - User doesn't have enough creatures
 *
 * @example
 * ```typescript
 * // Join open battle
 * const result = await joinBattle('battle-uuid-123');
 *
 * // Join closed battle with password
 * const result = await joinBattle('battle-uuid-456', 'secret123');
 *
 * navigate(`/deployment?matchId=${result.matchId}&playerId=${result.playerId}`);
 * ```
 */
export async function joinBattle(
  battleId: string,
  password?: string
): Promise<JoinBattleResponse> {
  return apiRequest<JoinBattleResponse>(`/api/battles/${battleId}/join`, {
    method: 'POST',
    body: JSON.stringify({
      ...(password && { password })
    })
  });
}

/**
 * Quick Play - Auto-match with existing battle or create new one
 *
 * @returns Quick play response indicating whether joined or created
 * @throws InsufficientCreaturesError - User doesn't have enough creatures
 * @throws ActiveBattleExistsError - User already in an active battle
 *
 * @example
 * ```typescript
 * const result = await quickPlay();
 * if (result.action === 'joined') {
 *   console.log('Matched with existing battle!');
 * } else {
 *   console.log('Created new battle, waiting for opponent...');
 * }
 * navigate(`/deployment?matchId=${result.matchId}&playerId=${result.playerId}`);
 * ```
 */
export async function quickPlay(): Promise<QuickPlayResponse> {
  return apiRequest<QuickPlayResponse>('/api/battles/quick-play', {
    method: 'POST'
  });
}

/**
 * Get list of open battles
 *
 * @param page - Page number (default: 1)
 * @param limit - Battles per page (default: 10)
 * @returns List of open battles with pagination info
 *
 * @example
 * ```typescript
 * const { battles, total } = await getOpenBattles(1, 20);
 * console.log(`Showing ${battles.length} of ${total} battles`);
 * ```
 */
export async function getOpenBattles(
  page: number = 1,
  limit: number = 10
): Promise<BattleListResponse> {
  return apiRequest<BattleListResponse>(
    `/api/battles/open?page=${page}&limit=${limit}`,
    {
      method: 'GET'
    }
  );
}

/**
 * Get user's battle history
 *
 * @param limit - Maximum number of battles to return (default: 10, max: 50)
 * @returns Battle history with stats
 *
 * @example
 * ```typescript
 * const { battles, stats } = await getBattleHistory(10);
 * console.log(`Record: ${stats.wins}W / ${stats.losses}L / ${stats.draws}D`);
 * battles.forEach(b => console.log(`${b.battleName}: ${b.userResult}`));
 * ```
 */
export async function getBattleHistory(
  limit: number = 10
): Promise<BattleHistoryResponse> {
  return apiRequest<BattleHistoryResponse>(
    `/api/battles/history?limit=${limit}`,
    {
      method: 'GET'
    }
  );
}
